import { useState, useEffect, useCallback, useMemo } from 'react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Modal, Spin, Select, Form, message } from 'antd'
import { GetConnectivityScore } from 'services/data-provider/listing'
import { ConnectivityScore } from 'models/listing'
import { Heading } from '../../atom/user-edit'
import { useChargerConnectivityTranslation } from '../../hooks/translation/useChargerConnectivityTranslation'

const ChargerConnectivityModal = ({ chargerId, isOpen, onClose, chargerTitle }: any) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  
  // Get local timezone abbreviation properly
  const timezoneName = useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
    const parts = formatter.formatToParts(new Date())
    const tzName = parts.find(part => part.type === 'timeZoneName')?.value || 'UTC'
    return tzName
  }, [timezone])

  const {
    connectivityPeriods,
    selectTimeframeText,
    timeText,
    scoreText,
    connectivityDetailsText,
  } = useChargerConnectivityTranslation()

  const PERIOD_VALUES = ['last_1_hour', 'last_1_day', 'last_7_days', 'last_8_weeks']
  const [period, setPeriod] = useState('last_1_hour')
  const [connectivityData, setConnectivityData] = useState<ConnectivityScore[]>([])
  const [loading, setLoading] = useState(false)

  // Convert UTC time strings/timestamps to local time/date based on period
  const convertToLocalTime = useCallback((utcTimeString: string, currentPeriod: string): string => {
    // Try to parse as ISO timestamp (UTC)
    let date: Date | null = null
    
    // Check if it's an ISO timestamp
    if (utcTimeString.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(utcTimeString)) {
      // Check if it already has timezone info (Z or +/- offset)
      if (utcTimeString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(utcTimeString)) {
        // Has timezone info, parse as is
        date = new Date(utcTimeString)
      } else {
        // No timezone info, assume UTC and append Z
        const utcString = `${utcTimeString}Z`
        date = new Date(utcString)
      }
    } else if (/^\d+$/.test(utcTimeString)) {
      // If it's a numeric timestamp (milliseconds or seconds)
      const num = parseInt(utcTimeString, 10)
      // If less than 13 digits, assume seconds and convert to milliseconds
      date = new Date(num < 10000000000 ? num * 1000 : num)
    } else {
      // Try direct date parsing
      date = new Date(utcTimeString)
    }

    if (!date || isNaN(date.getTime())) {
      // If we can't parse it, return as is (might already be formatted time string)
      return utcTimeString
    }

    // Format based on period: show date for last_7_days and last_8_weeks, time for others
    if (currentPeriod === 'last_7_days' || currentPeriod === 'last_8_weeks') {
      // Format as date (MM/DD format)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    } else {
      // Format as time (HH:MM AM/PM)
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    }
  }, [])

  const fetchData = useCallback(async () => {
    if (!isOpen) return
    setLoading(true)
    try {
      const connectivityScores: ConnectivityScore[] = await GetConnectivityScore(chargerId, period, timezone)
      // Convert UTC periods to local time/date based on period
      const convertedData = (connectivityScores || []).map(score => ({
        ...score,
        period: convertToLocalTime(score.period, period),
      }))
      setConnectivityData(convertedData)
    } catch (error) {
      console.error('Error fetching connectivity score:', error)
      message.error('Failed to load connectivity data')
    } finally {
      setLoading(false)
    }
  }, [isOpen, period, chargerId, timezone, convertToLocalTime])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return (
    <Modal
      title={<Heading>{`${connectivityDetailsText.replace('{{id}}', chargerTitle)}`}</Heading>}
      visible={isOpen}
      onCancel={onClose}
      footer={null}
      style={{ minHeight: '50%', minWidth: '50%' }}
    >
      <div className="flex items-center">
        <Form.Item label={selectTimeframeText} style={{ width: '50%' }}>
          <Select value={period} onChange={setPeriod} style={{ width: '100%' }}>
            {connectivityPeriods.map((label, index) => (
              <Select.Option key={index} value={PERIOD_VALUES[index]}>
                {label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="h-64">
            {connectivityData.length > 0 ? (
              <ResponsiveContainer minHeight={500} minWidth={290}>
                <LineChart data={connectivityData} margin={{ top: 20, right: 40, left: 60, bottom: 40 }}>
                  <XAxis
                    dataKey="period"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    interval={0}
                    tick={{ dy: 16 }}
                    label={{ 
                      value: period === 'last_7_days' || period === 'last_8_weeks' 
                        ? `Date (${timezoneName})` 
                        : `Time (${timezoneName})`, 
                      offset: 10, 
                      position: 'insideTop' 
                    }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    ticks={[0, 20, 40, 60, 80, 100]}
                    label={{ value: scoreText, angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="connectivity_score"
                    stroke="#ccc"
                    strokeWidth={2}
                    dot={<CustomDot />}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500">No connectivity data available.</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

const CustomDot = (props: any) => {
  const { cx, cy, value } = props
  let fillColor = 'red' // Default to Weak Connectivity
  if (value >= 80) fillColor = 'green'
  // Strong
  else if (value >= 60) fillColor = 'yellow' // Acceptable

  return <circle cx={cx} cy={cy} r={5} fill={fillColor} />
}

export default ChargerConnectivityModal
